import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'

import {
  buildColumnSliceState,
  columnHasPreviewPage,
  columnSliceBandHeightPx,
  twoColumnColumnViewportHeightPx,
  type ColumnSliceStateResult,
} from '../../utils/resumePreviewBreakSnap'
import { twoColumnColgroupPercents } from '../../utils/resumeExportPdfConfig'
import { RESUME_LINE_GUIDE_COLORS } from '../../utils/resumePreviewLineGuides'
import {
  computeResumePreviewPageMetrics,
  measureTwoColumnPreviewLayout,
  RESUME_PREVIEW_PAGE,
  twoColumnSheet1ReservedTopPx,
  type ResumePreviewPageMetrics,
  type TwoColumnPreviewLayout,
} from '../../utils/resumePreviewPageLayout'
import type { ResumePreviewColumnSlice } from './ResumePreviewColumnSliceContext'
import ResumePreviewPanelFit from './ResumePreviewPanelFit'
import ResumePreviewDebugContent from './ResumePreviewDebugContent'
import ResumePreviewDebugLegend from './ResumePreviewDebugLegend'
import { ResumePreviewFontSizeLabelsOverlay } from './ResumePreviewFontSizeLabelsContent'
import { ResumePreviewPageBreakEdge, ResumePreviewColumnZoneLabel } from './ResumePreviewLineGuidesOverlay'

type ColumnSliceState = ColumnSliceStateResult

function HeaderBandViewport({
  children,
  heightPx,
  lineGuides,
  fontSizeDebug,
}: {
  children: React.ReactNode
  heightPx: number
  lineGuides?: boolean
  fontSizeDebug?: boolean
}) {
  const clipRef = useRef<HTMLDivElement>(null)
  return (
    <div
      className={`shrink-0 relative${fontSizeDebug ? ' overflow-visible' : ' overflow-hidden'}`}
      style={{ height: heightPx }}
    >
      <div ref={clipRef} className="relative h-full w-full min-w-0 overflow-hidden">
        <ResumePreviewDebugContent lineGuides={lineGuides} guideScopeSelector="[data-paysafe-header]">
          {children}
        </ResumePreviewDebugContent>
      </div>
      {fontSizeDebug && (
        <ResumePreviewFontSizeLabelsOverlay
          enabled
          measureRootRef={clipRef}
          scopeSelector="[data-paysafe-header]"
          overlayHeightPx={heightPx}
        />
      )}
    </div>
  )
}

function withColumnSlice(
  children: React.ReactNode,
  columnSlice: Exclude<ResumePreviewColumnSlice, 'full'>
): React.ReactNode {
  if (!React.isValidElement(children)) return children
  return React.cloneElement(children, { columnSlice } as { columnSlice: typeof columnSlice })
}

function ColumnViewport({
  children,
  colState,
  pageIndex,
  viewportHeightPx,
  lineGuides,
  fontSizeDebug,
  columnSide,
}: {
  children: React.ReactNode
  colState: ColumnSliceState
  pageIndex: number
  viewportHeightPx: number
  lineGuides?: boolean
  fontSizeDebug?: boolean
  columnSide: 'left' | 'right'
}) {
  if (!columnHasPreviewPage(colState.starts, pageIndex)) {
    return (
      <div
        className="resume-preview-column-viewport resume-preview-column-viewport--empty"
        style={{ height: 0, overflow: 'hidden' }}
        aria-hidden
      />
    )
  }

  const start = colState.starts[pageIndex]!
  const leadPx = colState.leads[pageIndex] ?? 0
  const sliceBandPx = columnSliceBandHeightPx(
    colState.starts,
    colState.leads,
    pageIndex,
    colState.totalHeight
  )
  const contentClipH =
    sliceBandPx > 0 ? Math.min(viewportHeightPx, sliceBandPx) : viewportHeightPx

  if (viewportHeightPx <= 0 || contentClipH <= 0) {
    return (
      <div
        className="resume-preview-column-viewport resume-preview-column-viewport--empty"
        style={{ height: 0, overflow: 'hidden' }}
        aria-hidden
      />
    )
  }

  const contentClipRef = useRef<HTMLDivElement>(null)

  return (
    <div
      className={`resume-preview-column-viewport relative${
        fontSizeDebug ? ' overflow-visible' : ' overflow-hidden'
      }`}
      style={{
        width: '100%',
        minWidth: 0,
        height: viewportHeightPx,
        ...(lineGuides && columnSide === 'right'
          ? { boxShadow: `inset 4px 0 0 0 ${RESUME_LINE_GUIDE_COLORS.right}` }
          : null),
      }}
    >
      {lineGuides && <ResumePreviewColumnZoneLabel side={columnSide} />}
      <div
        ref={contentClipRef}
        className="relative w-full min-w-0 overflow-hidden"
        style={{ height: contentClipH }}
      >
        <div
          className="resume-preview-column-slice-inner"
          style={{
            width: '100%',
            transform: `translateY(-${start + leadPx}px)`,
          }}
        >
          <ResumePreviewDebugContent lineGuides={lineGuides}>{children}</ResumePreviewDebugContent>
        </div>
      </div>
      {fontSizeDebug && (
        <ResumePreviewFontSizeLabelsOverlay
          enabled
          measureRootRef={contentClipRef}
          overlayHeightPx={contentClipH}
        />
      )}
      {lineGuides && <ResumePreviewPageBreakEdge />}
    </div>
  )
}

type ResumePreviewTwoColumnPageGuidesProps = {
  children: React.ReactNode
  lineGuides?: boolean
  fontSizeDebug?: boolean
}

/**
 * Two-column preview: independent vertical slices per column (like PDF setPage per column).
 */
export default function ResumePreviewTwoColumnPageGuides({
  children,
  lineGuides = false,
  fontSizeDebug = false,
}: ResumePreviewTwoColumnPageGuidesProps) {
  const layoutMeasureRef = useRef<HTMLDivElement>(null)
  const leftColMeasureRef = useRef<HTMLDivElement>(null)
  const rightColMeasureRef = useRef<HTMLDivElement>(null)
  const [metrics, setMetrics] = useState(() => computeResumePreviewPageMetrics())
  const [headerHeight, setHeaderHeight] = useState(0)
  const [leftCol, setLeftCol] = useState<ColumnSliceState>({
    starts: [0],
    leads: [0],
    totalHeight: 0,
  })
  const [rightCol, setRightCol] = useState<ColumnSliceState>({
    starts: [0],
    leads: [0],
    totalHeight: 0,
  })
  const [colLayout, setColLayout] = useState<TwoColumnPreviewLayout>({
    columnsRowTopPx: 0,
    columnsTopPx: 0,
    leftTopPx: 0,
    rightTopPx: 0,
    leftShiftPx: 0,
    rightShiftPx: 0,
    leftWidthPx: 0,
    gapWidthPx: 0,
    rightWidthPx: 0,
  })

  const pageFrameStyle = useMemo(
    () => ({
      boxSizing: 'border-box' as const,
      width: '100%',
      height: '100%',
      paddingTop: `${RESUME_PREVIEW_PAGE.marginTopMm}mm`,
      paddingRight: `${RESUME_PREVIEW_PAGE.marginRightMm}mm`,
      paddingBottom: `${RESUME_PREVIEW_PAGE.marginBottomMm}mm`,
      paddingLeft: `${RESUME_PREVIEW_PAGE.marginLeftMm}mm`,
    }),
    []
  )

  const leftPreview = useMemo(() => withColumnSlice(children, 'left'), [children])
  const rightPreview = useMemo(() => withColumnSlice(children, 'right'), [children])

  const remeasure = useCallback(() => {
    const next = computeResumePreviewPageMetrics()
    setMetrics(next)
    const layoutRoot = layoutMeasureRef.current
    if (!layoutRoot) return

    const headerEl = layoutRoot.querySelector<HTMLElement>('[data-paysafe-header]')
    const headerH = headerEl?.offsetHeight ?? 0
    setHeaderHeight(headerH)

    const leftEl = layoutRoot.querySelector<HTMLElement>('.paysafe-col-left')
    const rightEl = layoutRoot.querySelector<HTMLElement>('.paysafe-col-right')
    const layout = measureTwoColumnPreviewLayout(layoutRoot, leftEl, rightEl)
    setColLayout(layout)
    const sheet1Reserved = twoColumnSheet1ReservedTopPx(headerH, layout)
    const firstColZone =
      sheet1Reserved > 0
        ? Math.max(0, next.contentZoneHeightPx - sheet1Reserved)
        : undefined

    const leftColEl =
      leftColMeasureRef.current?.querySelector<HTMLElement>('.paysafe-col-left') ??
      leftColMeasureRef.current
    const rightColEl =
      rightColMeasureRef.current?.querySelector<HTMLElement>('.paysafe-col-right') ??
      rightColMeasureRef.current

    setLeftCol(buildColumnSliceState(leftColEl, next.contentZoneHeightPx, firstColZone))
    setRightCol(buildColumnSliceState(rightColEl, next.contentZoneHeightPx, firstColZone))
  }, [])

  useLayoutEffect(() => {
    remeasure()
    if (typeof document !== 'undefined' && document.fonts?.ready) {
      void document.fonts.ready.then(() => remeasure())
    }
  }, [children, remeasure])

  useLayoutEffect(() => {
    const nodes = [
      layoutMeasureRef.current,
      leftColMeasureRef.current,
      rightColMeasureRef.current,
    ].filter(Boolean) as HTMLElement[]
    if (nodes.length === 0 || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(() => remeasure())
    nodes.forEach((el) => ro.observe(el))
    return () => ro.disconnect()
  }, [remeasure])

  const pageCount = Math.max(leftCol.starts.length, rightCol.starts.length, 1)
  const { pageWidthPx, pageHeightPx, contentWidthPx } = metrics
  const colPct = twoColumnColgroupPercents()
  const columnsGridStyle = useMemo(
    () => ({
      gridTemplateColumns: `${colPct.left} ${colPct.gap} ${colPct.right}`,
    }),
    [colPct.gap, colPct.left, colPct.right]
  )
  const sheet1ReservedTopPx = twoColumnSheet1ReservedTopPx(headerHeight, colLayout)
  const sheet1HeaderBandPx =
    pageCount > 0 && headerHeight > 0 ? sheet1ReservedTopPx : headerHeight

  return (
    <>
      <div className="resume-preview-measure" aria-hidden>
        <div
          ref={layoutMeasureRef}
          className="resume-preview-measure-inner"
          style={{ width: contentWidthPx > 0 ? contentWidthPx : '210mm' }}
        >
          {children}
        </div>
        <div
          className="resume-preview-columns-row"
          style={{
            width: contentWidthPx > 0 ? contentWidthPx : '210mm',
            gridTemplateColumns: `${colPct.left} ${colPct.gap} ${colPct.right}`,
          }}
        >
          <div ref={leftColMeasureRef} className="min-w-0">
            {leftPreview}
          </div>
          <div className="resume-preview-col-gap" aria-hidden />
          <div ref={rightColMeasureRef} className="min-w-0">
            {rightPreview}
          </div>
        </div>
      </div>

      <ResumePreviewPanelFit pageWidthPx={pageWidthPx}>
        <div className="resume-preview-paged-host w-full flex flex-col items-center">
          {(lineGuides || fontSizeDebug) && (
            <ResumePreviewDebugLegend lineGuides={lineGuides} fontSizeDebug={fontSizeDebug} />
          )}

          <div
            className="resume-preview-paged-stack flex flex-col items-center"
            style={{ width: pageWidthPx > 0 ? pageWidthPx : '210mm' }}
            data-resume-page-guides="two_column"
          >
        {Array.from({ length: pageCount }, (_, pageIndex) => {
          const leftHasPage = columnHasPreviewPage(leftCol.starts, pageIndex)
          const rightHasPage = columnHasPreviewPage(rightCol.starts, pageIndex)
          const sharedViewportHeightPx = twoColumnColumnViewportHeightPx(
            pageIndex,
            leftHasPage || rightHasPage,
            metrics.contentZoneHeightPx,
            sheet1ReservedTopPx,
          )

          return (
          <div
            key={pageIndex}
            className="resume-preview-a4-sheet resume-preview-a4-sheet--clipped box-border relative shrink-0 overflow-hidden"
            style={{
              width: pageWidthPx > 0 ? pageWidthPx : '210mm',
              height: pageHeightPx > 0 ? pageHeightPx : '297mm',
            }}
          >
            <div className="resume-preview-page-frame flex flex-col flex-1 min-h-0" style={pageFrameStyle}>
              {pageIndex === 0 && sheet1HeaderBandPx > 0 && (
                <HeaderBandViewport
                  heightPx={sheet1HeaderBandPx}
                  lineGuides={lineGuides}
                  fontSizeDebug={fontSizeDebug}
                >
                  {children}
                </HeaderBandViewport>
              )}
              <div
                className="resume-preview-columns-row w-full flex-1 min-h-0"
                style={columnsGridStyle}
              >
                <ColumnViewport
                  colState={leftCol}
                  pageIndex={pageIndex}
                  viewportHeightPx={leftHasPage ? sharedViewportHeightPx : 0}
                  lineGuides={lineGuides}
                  fontSizeDebug={fontSizeDebug}
                  columnSide="left"
                >
                  {leftPreview}
                </ColumnViewport>
                <div className="resume-preview-col-gap" aria-hidden />
                <ColumnViewport
                  colState={rightCol}
                  pageIndex={pageIndex}
                  viewportHeightPx={rightHasPage ? sharedViewportHeightPx : 0}
                  lineGuides={lineGuides}
                  fontSizeDebug={fontSizeDebug}
                  columnSide="right"
                >
                  {rightPreview}
                </ColumnViewport>
              </div>
            </div>
          </div>
          )
        })}
          </div>
        </div>
      </ResumePreviewPanelFit>
    </>
  )
}
